// src/components/ClassesFraisManagement.js
import React, { useState, useEffect } from 'react';
import { collection, addDoc, onSnapshot, query, orderBy, serverTimestamp, deleteDoc, doc, updateDoc, where, getDocs } from 'firebase/firestore';

// Définition des options pour les champs de la classe
const BASE_NOTATION_OPTIONS = [10, 20];
const SERIE_OPTIONS = ['', 'L','S', 'L1', 'L2', 'S1', 'S2', 'S3', 'S4', 'G', 'T']; // Ajout d'une option vide
const NIVEAU_OPTIONS = ['', 'préscolaire', 'élémentaire', 'collège', 'lycée']; // Ajout d'une option vide

const ClassesFraisManagement = ({ db, setError, setMessage, schoolId, userRole }) => {
  const [classes, setClasses] = useState([]);
  const [nomClasse, setNomClasse] = useState('');
  const [montantInscription, setMontantInscription] = useState('');
  const [montantMensualite, setMontantMensualite] = useState('');
  const [descriptionFraisSupplementaires, setDescriptionFraisSupplementaires] = useState('');
  
  // Nouveaux états pour les champs de métadonnées
  const [baseNotation, setBaseNotation] = useState(BASE_NOTATION_OPTIONS[1]); // Par défaut à 20
  const [serie, setSerie] = useState('');
  const [niveau, setNiveau] = useState('');
  
  const [isEditing, setIsEditing] = useState(false);
  const [currentClasseId, setCurrentClasseId] = useState(null);

  useEffect(() => {
    if (!db) {
      console.error("Firestore DB instance is not provided to ClassesFraisManagement.");
      setError("Erreur de connexion à la base de données. Veuillez recharger la page.");
      return;
    }

    // Si pas de schoolId (par exemple, Super Admin n'a pas sélectionné d'école), ne rien faire
    if (!schoolId) {
      setClasses([]); // Réinitialiser les classes si l'école change ou n'est plus sélectionnée
      return;
    }

    // Récupérer les classes spécifiques à cette école
    const classesCollectionRef = collection(db, "schools", schoolId, "classes");
    const q = query(classesCollectionRef, orderBy("nom", "asc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const classesData = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        // Assurer que les nouveaux champs existent avec une valeur par défaut pour l'affichage
        baseNotation: doc.data().baseNotation || BASE_NOTATION_OPTIONS[1],
        serie: doc.data().serie || '',
        niveau: doc.data().niveau || '',
      }));
      setClasses(classesData);
    }, (error) => {
      console.error("Erreur lors de la récupération des classes:", error);
      setError("Erreur lors du chargement des classes.");
    });

    return () => unsubscribe();
  }, [db, setError, schoolId]);

  const resetForm = () => {
    setIsEditing(false);
    setCurrentClasseId(null);
    setNomClasse('');
    setMontantInscription('');
    setMontantMensualite('');
    setDescriptionFraisSupplementaires('');
    // Réinitialisation des nouveaux champs
    setBaseNotation(BASE_NOTATION_OPTIONS[1]);
    setSerie('');
    setNiveau('');
  };

  const handleSaveClasseFrais = async (e) => {
    e.preventDefault();
    if (!nomClasse || !montantInscription || !montantMensualite || !niveau) { // Ajout de 'niveau' comme obligatoire
      setError("Veuillez remplir tous les champs obligatoires (Nom de la Classe, Montant Inscription, Montant Mensualité, Niveau Scolaire).");
      return;
    }
    
    // Vérifier si schoolId est disponible avant de sauvegarder
    if (!schoolId) {
      setError("Impossible de sauvegarder : aucune école sélectionnée. Veuillez sélectionner une école.");
      return;
    }

    try {
      const dataToSave = {
        nom: nomClasse,
        montantInscription: parseFloat(montantInscription),
        montantMensualite: parseFloat(montantMensualite),
        descriptionFraisSupplementaires: descriptionFraisSupplementaires,
        // Ajout des nouveaux champs
        baseNotation: parseInt(baseNotation, 10),
        serie: serie,
        niveau: niveau,
        // Métadonnées
        updatedAt: serverTimestamp(),
      };

      if (isEditing) {
        const classeRef = doc(db, 'schools', schoolId, 'classes', currentClasseId);
        await updateDoc(classeRef, dataToSave);
        setMessage("Classe et frais mis à jour avec succès !");
      } else {
        // Vérifier si une classe avec ce nom existe déjà dans cette école
        const classesCollectionRef = collection(db, "schools", schoolId, "classes");
        const existingClassesQuery = query(classesCollectionRef, where("nom", "==", nomClasse));
        const existingClassesSnapshot = await getDocs(existingClassesQuery);

        if (!existingClassesSnapshot.empty) {
          setError(`Une classe nommée "${nomClasse}" existe déjà dans cette école.`);
          return;
        }

        await addDoc(collection(db, 'schools', schoolId, 'classes'), {
          ...dataToSave,
          createdAt: serverTimestamp(),
        });
        setMessage("Classe et frais ajoutés avec succès !");
      }
      resetForm();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de la classe et des frais:", error);
      setError(`Échec de la sauvegarde de la classe et des frais : ${error.message}`);
    }
  };

  const handleEdit = (classe) => {
    setIsEditing(true);
    setCurrentClasseId(classe.id);
    setNomClasse(classe.nom);
    setMontantInscription(classe.montantInscription.toString());
    setMontantMensualite(classe.montantMensualite.toString());
    setDescriptionFraisSupplementaires(classe.descriptionFraisSupplementaires || '');
    // Chargement des nouveaux champs
    setBaseNotation(classe.baseNotation || BASE_NOTATION_OPTIONS[1]);
    setSerie(classe.serie || '');
    setNiveau(classe.niveau || '');
  };

  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette classe et ses paramètres financiers ?")) {
      try {
        await deleteDoc(doc(db, 'schools', schoolId, 'classes', id));
        setMessage("Classe et frais supprimés avec succès !");
      } catch (error) {
        console.error("Erreur lors de la suppression de la classe et des frais:", error);
        setError(`Échec de la suppression de la classe et des frais : ${error.message}`);
      }
    }
  };


  // Condition de rendu pour Super Admin sans école sélectionnée
  if (userRole === 'superAdmin' && !schoolId) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 mb-4">
        <p className="font-semibold">Veuillez sélectionner une école dans le menu déroulant "Gérer l'école" pour accéder à la gestion des classes et des frais.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Gestion des Classes et Frais Scolaires</h2>

      {/* Formulaire d'ajout/modification */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">{isEditing ? 'Modifier une Classe Existante' : 'Ajouter une Nouvelle Classe'}</h3>
        <form onSubmit={handleSaveClasseFrais} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* 1. Nom de la Classe */}
          <div>
            <label htmlFor="nomClasse" className="block text-sm font-medium text-gray-700">Nom de la Classe</label>
            <input
              type="text"
              id="nomClasse"
              value={nomClasse}
              onChange={(e) => setNomClasse(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              required
              
            />
          </div>
          
          {/* 2. Niveau Scolaire - NOUVEAU */}
          <div>
            <label htmlFor="niveau" className="block text-sm font-medium text-gray-700">Niveau Scolaire</label>
            <select
              id="niveau"
              value={niveau}
              onChange={(e) => setNiveau(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              required
            >
              {NIVEAU_OPTIONS.map(option => (
                <option key={option} value={option}>{option === '' ? '— Sélectionner —' : option.charAt(0).toUpperCase() + option.slice(1)}</option>
              ))}
            </select>
          </div>

          {/* 3. Notation de Base (10 ou 20) - NOUVEAU */}
          <div>
            <label htmlFor="baseNotation" className="block text-sm font-medium text-gray-700">Base de Notation</label>
            <select
              id="baseNotation"
              value={baseNotation}
              onChange={(e) => setBaseNotation(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              required
            >
              {BASE_NOTATION_OPTIONS.map(option => (
                <option key={option} value={option}>{option}/20</option>
              ))}
            </select>
          </div>

          {/* 4. Série - NOUVEAU */}
          <div>
            <label htmlFor="serie" className="block text-sm font-medium text-gray-700">Série (Optionnel)</label>
            <select
              id="serie"
              value={serie}
              onChange={(e) => setSerie(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              {SERIE_OPTIONS.map(option => (
                <option key={option} value={option}>{option === '' ? '— Aucune —' : option}</option>
              ))}
            </select>
          </div>
          
          {/* 5. Montant d'Inscription (XOF) */}
          <div>
            <label htmlFor="montantInscription" className="block text-sm font-medium text-gray-700">Montant d'Inscription (XOF)</label>
            <input
              type="number"
              id="montantInscription"
              value={montantInscription}
              onChange={(e) => setMontantInscription(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              step="0.01"
              required
            />
          </div>
          
          {/* 6. Montant Mensualité (XOF) */}
          <div>
            <label htmlFor="montantMensualite" className="block text-sm font-medium text-gray-700">Montant Mensualité (XOF)</label>
            <input
              type="number"
              id="montantMensualite"
              value={montantMensualite}
              onChange={(e) => setMontantMensualite(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              step="0.01"
              required
            />
          </div>
          
          {/* 7. Description Frais Supplémentaires (optionnel) */}
          <div className="md:col-span-2"> {/* Ce champ prend 2 colonnes pour plus d'espace */}
            <label htmlFor="descriptionFraisSupplementaires" className="block text-sm font-medium text-gray-700">Description Frais Supplémentaires (optionnel)</label>
            <input
              type="text"
              id="descriptionFraisSupplementaires"
              value={descriptionFraisSupplementaires}
              onChange={(e) => setDescriptionFraisSupplementaires(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>

          {/* Boutons d'action */}
          <div className="col-span-1 md:col-span-2 flex justify-end space-x-3">
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {isEditing ? 'Modifier la Classe' : 'Ajouter la Classe'}
            </button>
          </div>
        </form>
      </div>

      {/* Liste des classes et leurs frais */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">Classes Existantes et leurs Frais</h3>
        {classes.length === 0 ? (
          <p className="text-gray-600">Aucune classe configurée pour le moment.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom de la Classe
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Niveau
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Série
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notation
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant Inscription (XOF)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant Mensualité (XOF)
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Frais Supplémentaires
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {classes.map((classe) => (
                  <tr key={classe.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {classe.nom}
                    </td>
                    {/* Affichage des nouveaux champs */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {classe.niveau.charAt(0).toUpperCase() + classe.niveau.slice(1) || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{classe.serie || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{classe.baseNotation}/20</td>
                    {/* Fin des nouveaux champs */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{classe.montantInscription}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{classe.montantMensualite}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{classe.descriptionFraisSupplementaires || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(classe)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        Éditer
                      </button>
                      <button
                        onClick={() => handleDelete(classe.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassesFraisManagement;